import { useState } from "react";
import { Check, Loader2, TicketPercent, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, useApplyCoupon, useValidateCoupon } from "../api/useCoupons";
import type { CouponValidation, DealCoupon } from "../types";

function fmt(n: number) {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/**
 * "Discount → Coupon code" on a deal's Payments card.
 *
 * The counsellor types a code and presses Check: the server works the discount
 * out from the deal's value (never the browser) and answers either the numbers
 * or the reason it can't be used. Apply sends only the code — the server checks
 * everything again and is the one that writes.
 */
export default function DealCouponPanel({
  childId,
  appliedCoupon,
  onDone,
  onCancel,
}: {
  childId: number;
  /** The coupon already on this deal, if any (only one at a time). */
  appliedCoupon: DealCoupon | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [result, setResult] = useState<CouponValidation | null>(null);
  const validate = useValidateCoupon();
  const apply = useApplyCoupon();

  if (appliedCoupon) {
    return (
      <div className="flex items-start justify-between gap-2 rounded-lg border border-[#e0c6ff] bg-[#faf8ff] px-3 py-2">
        <p className="text-[11px] text-[#6d28d9] leading-[1.45]">
          Coupon <span className="font-semibold">{appliedCoupon.code}</span> is
          already on this deal. Remove it (above) to use a different code — a
          deal takes one coupon at a time.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="h-7 text-[11px] px-3 rounded-[8px] shrink-0"
        >
          Close
        </Button>
      </div>
    );
  }

  const check = () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    validate.mutate(
      { code: trimmed, childId },
      {
        onSuccess: (r) => setResult(r),
        onError: (e: unknown) =>
          setResult({
            valid: false,
            reason: "ERROR",
            message: errMsg(e, "Couldn't check this code."),
          }),
      }
    );
  };

  const doApply = () => {
    if (!result || !result.valid) return;
    apply.mutate(
      { childId, code: result.coupon.code },
      {
        onSuccess: () => {
          setCode("");
          setResult(null);
          onDone();
        },
        onError: () => setResult(null),
      }
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <TicketPercent className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#8b5cf6]" />
          <input
            type="text"
            autoFocus
            placeholder="Enter coupon code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setResult(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                check();
              }
            }}
            className="h-9 w-full pl-8 pr-3 text-[13px] font-medium tracking-[0.04em] rounded-[8px] border border-[#8b5cf6]/30 bg-[#faf8ff] text-[#374741] placeholder:text-[#a78bfa] placeholder:tracking-normal placeholder:font-normal focus:border-[#8b5cf6] focus:outline-none focus:ring-2 focus:ring-[#8b5cf6]/10 transition-all"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!code.trim() || validate.isPending}
          onClick={check}
          className="h-9 text-[12px] px-4 rounded-[8px] font-medium"
        >
          {validate.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            "Check"
          )}
        </Button>
      </div>

      {result && !result.valid && (
        <div className="flex items-start gap-1.5 rounded-lg bg-[#fff1f2] border border-[#fecdd3] px-2.5 py-2">
          <X className="size-3.5 text-[#be123c] mt-[1px] shrink-0" />
          <p className="text-[11px] text-[#9f1239] leading-[1.45]">
            {result.message}
          </p>
        </div>
      )}

      {result && result.valid && (
        <div className="rounded-lg border border-[#c1e1d5] bg-[#f0faf5] px-3 py-2 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[12px] text-[#1a6b4c]">
            <Check className="size-3.5 shrink-0" />
            <span className="font-semibold">{result.coupon.code}</span>
            <span className="text-[#2d7a5a]">· {result.coupon.summary}</span>
          </div>
          {result.coupon.description && (
            <p className="text-[10px] text-[#4b6b5e]">
              {result.coupon.description}
            </p>
          )}
          <div className="grid grid-cols-3 gap-x-4 gap-y-0.5 pt-1 text-[11px] tabular-nums">
            <span className="text-[#697571]">Deal value</span>
            <span className="text-[#697571]">Coupon</span>
            <span className="text-[#697571]">Net</span>
            <span className="font-semibold text-[#1e302a]">
              ₹{fmt(result.grossAmount)}
            </span>
            <span className="font-semibold text-[#be123c]">
              −₹{fmt(result.discountAmount)}
            </span>
            <span className="font-bold text-[#1a6b4c]">
              ₹{fmt(result.netAmount)}
            </span>
          </div>
          {result.replacesDiscount > 0 && (
            <p className="text-[10px] text-[#9a3412] pt-0.5">
              Replaces the deal's current ₹{fmt(result.replacesDiscount)}{" "}
              discount (it comes back if the coupon is removed).
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-0.5">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="h-8 text-[12px] px-4 rounded-[8px] font-medium"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!result?.valid || apply.isPending}
          onClick={doApply}
          className="h-8 text-[12px] px-4 rounded-[8px] font-medium !bg-[#7c3aed] hover:!bg-[#6d28d9]"
        >
          {apply.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : result?.valid ? (
            `Apply −₹${fmt(result.discountAmount)}`
          ) : (
            "Apply coupon"
          )}
        </Button>
      </div>
    </div>
  );
}
